import { PageHeader } from "@bednbody/ui/components/page-header";
import { Empty } from "@bednbody/ui/components/empty";
import { ChartBarIcon } from "@phosphor-icons/react";
import { StatTile } from "@bednbody/ui/components/stat-tile";
import { Skeleton } from "@bednbody/ui/components/skeleton";

import { Callout } from "@bednbody/ui/components/callout";
import { Button } from "@bednbody/ui/components/button";
import { RetryButton } from "@bednbody/ui/components/retry-button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bednbody/ui/components/dialog";
import { Badge } from "@bednbody/ui/components/badge";
import { Input } from "@bednbody/ui/components/input";
import { cn } from "@bednbody/ui/lib/utils";
import { PencilSimpleIcon, PlusIcon, TagIcon } from "@phosphor-icons/react";
import { type FormEvent, type ReactNode, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import {
  ServiceCurationControls,
  ServiceStateBadges,
  VariantActiveToggle,
} from "./CatalogItemControls";
import {
  type CatalogService,
  type ServiceInput,
  useCatalog,
  useCreateService,
  useCreateVariant,
  useUpdateService,
  useUpdateVariant,
  servicePhotoProblem,
  servicePhotoUrl,
  useUploadServicePhoto,
} from "./catalog.hooks";

const peso = (n: number) => `₱${n.toLocaleString("en-PH", { minimumFractionDigits: 2 })}`;

const num = (form: FormData, key: string) => Number(form.get(key) ?? 0);
const str = (form: FormData, key: string) => String(form.get(key) ?? "").trim();

type DialogState =
  | { kind: "service-new" }
  | { kind: "service-edit"; service: CatalogService }
  | { kind: "variant-new"; service: CatalogService }
  | {
      kind: "variant-edit";
      variant: CatalogService["service_variants"][number];
      serviceName: string;
    }
  | null;

/**
 * Service catalog — the price source every money computation reads
 * (promo_price is the ACTIVE price; original_price is strikethrough display).
 * Route-gated to admin/superadmin (App.tsx); writes are the plain RLS grants
 * from 0004. No deletes: bookings reference variants forever, so a removed
 * service or duration is switched off (0281/0331), shown marked, and can be
 * restored. Remove/Restore and Popular/Featured are Super Admin only (0331).
 *
 * NO ADD-ONS. Luigi, 2026-09-06: "we don't have add-ons anymore"; the release retire migration (supabase/pending) retires
 * every row, and the section that created and edited them is gone.
 */
export function CatalogPage() {
  const { data: services, isLoading, isError, refetch } = useCatalog();
  // 0331: the database refuses the switches from anyone else; this only decides
  // whether to offer them.
  const { claims } = useAuth();
  const canCurate = claims?.role === "superadmin";
  const [dialog, setDialog] = useState<DialogState>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);

  const createService = useCreateService();
  const updateService = useUpdateService();
  const createVariant = useCreateVariant();
  const updateVariant = useUpdateVariant();
  const uploadPhoto = useUploadServicePhoto();

  const byCategory = useMemo(() => {
    const groups = new Map<string, CatalogService[]>();
    for (const s of services ?? []) {
      const list = groups.get(s.category) ?? [];
      list.push(s);
      groups.set(s.category, list);
    }
    return [...groups.entries()];
  }, [services]);

  // On sale only: a removed duration, or every duration of a removed service,
  // is listed for Restore but sells nothing.
  const variantCount = (services ?? []).reduce(
    (n, s) => n + (s.is_active ? s.service_variants.filter((v) => v.active).length : 0),
    0,
  );
  const anyPending =
    createService.isPending ||
    updateService.isPending ||
    createVariant.isPending ||
    updateVariant.isPending ||
    uploadPhoto.isPending;
  const mutationError =
    createService.error ??
    updateService.error ??
    createVariant.error ??
    updateVariant.error ??
    uploadPhoto.error;

  /**
   * ⚠ THE .catch HERE USED TO BE EMPTY, and it swallowed every save failure in
   * this screen. An admin edited a price, tapped Save, and the dialog simply
   * stayed open — no toast, no message, no spinner. Nothing distinguished
   * "refused by RLS" from "the click missed", so the reasonable next move was
   * to tap again.
   *
   * The dialog now stays open ON PURPOSE when a save fails: the values are
   * still in the form, so a retry does not mean retyping a price. Closing it
   * would throw away what they entered along with the error.
   */
  const closeAnd = (p: Promise<unknown>) =>
    p
      .then(() => {
        setDialog(null);
        setPhotoFile(null);
      })
      .catch((error: unknown) => {
        toast.error(error instanceof Error ? error.message : "Couldn't save that change.");
      });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!dialog) return;
    const form = new FormData(e.currentTarget);

    switch (dialog.kind) {
      case "service-new": {
        const input: ServiceInput = {
          category: str(form, "category"),
          name: str(form, "name"),
          blurb: str(form, "blurb") || null,
        };
        closeAnd(createService.mutateAsync(input));
        break;
      }
      case "service-edit": {
        const serviceId = dialog.service.id;
        const file = photoFile;
        // Text first, photo second: a refused photo still leaves the name and
        // blurb saved, and the error toast names what failed.
        closeAnd(
          updateService
            .mutateAsync({
              id: serviceId,
              category: str(form, "category"),
              name: str(form, "name"),
              blurb: str(form, "blurb") || null,
            })
            .then(() => (file ? uploadPhoto.mutateAsync({ serviceId, file }) : undefined)),
        );
        break;
      }
      case "variant-new":
        closeAnd(
          createVariant.mutateAsync({
            service_id: dialog.service.id,
            duration_minutes: num(form, "duration_minutes"),
            original_price: num(form, "original_price"),
            promo_price: num(form, "promo_price"),
          }),
        );
        break;
      case "variant-edit":
        closeAnd(
          updateVariant.mutateAsync({
            id: dialog.variant.id,
            duration_minutes: num(form, "duration_minutes"),
            original_price: num(form, "original_price"),
            promo_price: num(form, "promo_price"),
          }),
        );
        break;
    }
  };

  return (
    <div className="min-w-0 space-y-8">
      <PageHeader
        actions={
          <Button onClick={() => setDialog({ kind: "service-new" })}>
            <PlusIcon size={15} /> Add service
          </Button>
        }
        description="Promo price is the active price for every calculation — changes apply to new bookings only."
        title="Service Catalog"
      />

      {isError && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          <p>Couldn't load the catalog — check your connection and try again.</p>
          <RetryButton className="mt-3" size="sm" onRetry={() => refetch()}>
            Try again
          </RetryButton>
        </div>
      )}
      {mutationError && (
        <Callout tone="danger" role="alert">
          {(mutationError as Error).message}
        </Callout>
      )}

      <div className="grid grid-cols-2 gap-4">
        {(
          [
            ["Services", services?.length ?? 0],
            ["Variants", variantCount],
          ] as const
        ).map(([label, value]) => (
          <StatTile layout="compact" label={label} icon={ChartBarIcon} key={label}>
            <p className="text-2xl font-semibold tabular-nums text-primary-dark dark:text-primary-light">
              {isLoading ? "…" : value}
            </p>
          </StatTile>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-28 motion-reduce:animate-none" />
          ))}
        </div>
      ) : (services ?? []).length === 0 ? (
        <Empty>
          <p className="text-sm font-medium text-foreground">No services yet</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Add the first service to build the booking catalog.
          </p>
        </Empty>
      ) : (
        byCategory.map(([category, list]) => (
          <section key={category} className="space-y-3">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <TagIcon size={15} className="text-primary" /> {category}
              <span className="font-normal text-muted-foreground">· {list.length}</span>
            </h2>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {list.map((service) => (
                <div
                  key={service.id}
                  className={cn(
                    "rounded-2xl border border-border bg-card p-4 shadow-xs",
                    !service.is_active && "border-dashed bg-muted/40",
                  )}
                >
                  <div className="mb-3 flex items-start justify-between gap-2">
                    <div className="flex min-w-0 items-start gap-3">
                      {service.image_path ? (
                        <img
                          src={servicePhotoUrl(service.image_path) ?? undefined}
                          alt=""
                          className="size-12 shrink-0 rounded-lg object-cover"
                        />
                      ) : null}
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-foreground">
                          {service.name}
                        </p>
                        <ServiceStateBadges service={service} />
                        {service.blurb ? (
                          <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                            {service.blurb}
                          </p>
                        ) : null}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      type="button"
                      onClick={() => setDialog({ kind: "service-edit", service })}
                      className="shrink-0"
                      aria-label={`Edit ${service.name}`}
                    >
                      <PencilSimpleIcon />
                    </Button>
                  </div>

                  {service.service_variants.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No variants yet.</p>
                  ) : (
                    <ul className="divide-y divide-border/60">
                      {service.service_variants.map((v) => (
                        <li key={v.id} className="flex items-center justify-between gap-2 py-1.5">
                          <span className="flex items-center gap-2 text-sm text-muted-foreground">
                            {v.duration_minutes} min
                            {v.active ? null : <Badge variant="muted">Removed</Badge>}
                          </span>
                          <span className="flex items-center gap-2">
                            <s className="text-xs tabular-nums text-muted-foreground/70">
                              {peso(v.original_price)}
                            </s>
                            <span className="text-sm font-semibold tabular-nums text-foreground">
                              {peso(v.promo_price)}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              type="button"
                              onClick={() =>
                                setDialog({
                                  kind: "variant-edit",
                                  variant: v,
                                  serviceName: service.name,
                                })
                              }
                              aria-label={`Edit ${service.name} ${v.duration_minutes} min`}
                            >
                              <PencilSimpleIcon />
                            </Button>
                            {canCurate ? (
                              <VariantActiveToggle variant={v} serviceName={service.name} />
                            ) : null}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <Button
                    variant="ghost"
                    size="sm"
                    type="button"
                    onClick={() => setDialog({ kind: "variant-new", service })}
                    className="mt-2"
                  >
                    + Add variant
                  </Button>
                  {canCurate ? <ServiceCurationControls service={service} /> : null}
                </div>
              ))}
            </div>
          </section>
        ))
      )}

      {/* One dialog, mode-driven */}
      <Dialog
        open={!!dialog}
        onOpenChange={(open) => {
          if (!open) {
            setDialog(null);
            setPhotoFile(null);
          }
        }}
      >
        <DialogContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <DialogHeader>
              <DialogTitle>
                {dialog?.kind === "service-new" && "Add service"}
                {dialog?.kind === "service-edit" && `Edit ${dialog.service.name}`}
                {dialog?.kind === "variant-new" && `Add variant — ${dialog.service.name}`}
                {dialog?.kind === "variant-edit" &&
                  `Edit ${dialog.serviceName} · ${dialog.variant.duration_minutes} min`}
              </DialogTitle>
              {(dialog?.kind === "variant-new" || dialog?.kind === "variant-edit") && (
                <DialogDescription>
                  Promo price becomes the active price for new bookings immediately; placed bookings
                  keep the price they were created with.
                </DialogDescription>
              )}
            </DialogHeader>

            {(dialog?.kind === "service-new" || dialog?.kind === "service-edit") && (
              <div className="space-y-3">
                <Field label="Category">
                  <Input
                    name="category"
                    required
                    defaultValue={dialog.kind === "service-edit" ? dialog.service.category : ""}
                    placeholder="Relaxation / Therapeutic / Focus Relief / Special Care"
                  />
                </Field>
                <Field label="Name">
                  <Input
                    name="name"
                    required
                    defaultValue={dialog.kind === "service-edit" ? dialog.service.name : ""}
                  />
                </Field>
                <Field label="Blurb (optional)">
                  <Input
                    name="blurb"
                    defaultValue={
                      dialog.kind === "service-edit" ? (dialog.service.blurb ?? "") : ""
                    }
                  />
                </Field>
                {dialog.kind === "service-edit" ? (
                  <PhotoField
                    currentUrl={servicePhotoUrl(dialog.service.image_path)}
                    file={photoFile}
                    onPick={setPhotoFile}
                  />
                ) : null}
              </div>
            )}

            {(dialog?.kind === "variant-new" || dialog?.kind === "variant-edit") && (
              <PriceFields
                defaults={
                  dialog.kind === "variant-edit"
                    ? dialog.variant
                    : { duration_minutes: 60, original_price: 0, promo_price: 0 }
                }
              />
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialog(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={anyPending}>
                {anyPending ? "Saving…" : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

/**
 * The photo picker in the service dialog. Shows the chosen file when there is
 * one, otherwise the stored photo, otherwise an empty frame — and says plainly
 * that no photo keeps the picture the app already shows.
 */
function PhotoField({
  currentUrl,
  file,
  onPick,
}: {
  currentUrl: string | null;
  file: File | null;
  onPick: (file: File | null) => void;
}) {
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const shown = preview ?? currentUrl;

  return (
    <div>
      <p className="mb-1 text-xs font-medium text-muted-foreground">Photo</p>
      <div className="flex items-start gap-3">
        {shown ? (
          <img src={shown} alt="" className="size-16 shrink-0 rounded-lg object-cover" />
        ) : (
          <div className="flex size-16 shrink-0 items-center justify-center rounded-lg border border-dashed border-border text-xs text-muted-foreground">
            None
          </div>
        )}
        <label className="block min-w-0 flex-1 space-y-1">
          <span className="sr-only">Upload photo</span>
          <Input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(event) => {
              const picked = event.target.files?.[0] ?? null;
              if (picked) {
                const problem = servicePhotoProblem(picked);
                if (problem) {
                  toast.error(problem);
                  event.target.value = "";
                  onPick(null);
                  return;
                }
              }
              onPick(picked);
            }}
          />
          <span className="block text-xs text-muted-foreground">
            JPG, PNG or WebP, up to 5 MB. No photo keeps the app&apos;s current picture.
          </span>
        </label>
      </div>
    </div>
  );
}

function PriceFields({
  defaults,
}: {
  defaults: { duration_minutes?: number; original_price: number; promo_price: number };
}) {
  return (
    <div className="grid grid-cols-3 gap-3">
      <Field label="Minutes">
        <Input
          name="duration_minutes"
          type="number"
          min={0}
          step={15}
          required
          defaultValue={defaults.duration_minutes ?? ""}
        />
      </Field>
      <Field label="Original ₱">
        <Input
          name="original_price"
          type="number"
          min={0}
          step="0.01"
          required
          defaultValue={defaults.original_price || ""}
        />
      </Field>
      <Field label="Promo ₱ (active)">
        <Input
          name="promo_price"
          type="number"
          min={0}
          step="0.01"
          required
          defaultValue={defaults.promo_price || ""}
        />
      </Field>
    </div>
  );
}
